"use client";

import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import Image from "next/image";
import {
  centerCheck,
  USDCIcon,
  BTCIcon,
  USDTIcon,
  GHANAIcon,
  NGNIcon,
  KenyaIcon,
} from "./Images";
import ComingSoonBadge from "./badge";
import MapLinesSVG from "./MapLinesSVG";
import AnimatedDots from "./AnimatedDots";
import { Highlighter } from "../ui/highlighter";

export function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <section
      className={`relative bg-[url('/success_bg.svg')] w-full bg-cover min-h-[100vh] ${
        isScrolled ? "pt-16 sm:pt-28 md:pt-36" : "pt-16 sm:pt-18 md:pt-20"
      } pb-20 flex items-center flex-col justify-center font-inter px-4`}
    >
      <div className="lg:my-12 my-8 max-w-[856px] w-full mx-auto max-h-[241px] relative flex justify-center items-center">
        {/* layout */}
        <div className="w-full h-full relative">
          <MapLinesSVG />

          {/* Animated dots following paths */}
          <AnimatedDots />
        </div>

        {/* overlay */}
        <div className="absolute inset-0 w-full h-full flex justify-center items-center">
          <div className="w-full h-full relative flex justify-center items-center">
            {/* center */}
            <span className="relative lg:top-[20px] md:top-[20px] top-[10px] flex lg:size-[160px] md:size-[120px] size-[60px]">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#DBF7FF] opacity-85" />
              <Image
                src={centerCheck}
                alt="logo"
                className="w-full h-full"
                width={480}
                height={480}
                priority
                quality={100}
              />
            </span>

            {/* USDC Icon */}
            <span className="absolute lg:-top-[40px] md:-top-[30px] -top-[20px] lg:left-[20px] left-[10px] flex lg:size-[80px] md:size-[60px] size-[40px]">
              <Image
                src={USDCIcon}
                alt="usdc logo"
                className="w-full h-full"
                width={240}
                height={240}
                priority
                quality={100}
              />
            </span>

            {/* Bitcoin */}
            <span className="absolute lg:top-[60px] md:top-[60px] top-[30px] lg:-left-[120px] -left-[20px] flex lg:size-[160px] md:size-[120px] size-[60px]">
              <Image
                src={BTCIcon}
                alt="usdc logo"
                className="w-full h-full"
                width={480}
                height={480}
                priority
                quality={100}
              />
            </span>

            {/* USDT Icon */}
            <span className="absolute lg:-bottom-[40px] md:-bottom-[30px] -bottom-[20px] lg:left-[80px] md:left-[80px] left-[30px] flex lg:size-[80px] md:size-[60px] size-[40px]">
              <Image
                src={USDTIcon}
                alt="usdc logo"
                className="w-full h-full"
                width={240}
                height={240}
                priority
                quality={100}
              />
            </span>

            {/* Ghana */}
            <div className="absolute lg:-top-[40px] -top-[20px] md:right-[80px] right-[10px]">
              <div className="relative">
                <span className="flex lg:size-[80px] md:size-[60px] size-[40px]">
                  <Image
                    src={GHANAIcon}
                    alt="ghana logo"
                    className="w-full h-full"
                    width={240}
                    height={240}
                    priority
                    quality={100}
                  />
                </span>
                {/* badge */}
                <ComingSoonBadge
                  gradColor1="#FFDA7C33"
                  gradColor2="#FFE78F2B"
                  BgColor="#FFCE31"
                >
                  Coming Soon
                </ComingSoonBadge>
              </div>
            </div>

            {/* NGN */}
            <span className="absolute lg:top-[60px] md:top-[70px] top-[30px] lg:-right-[120px] -right-[20px] flex lg:size-[160px] md:size-[100px] size-[50px]">
              <Image
                src={NGNIcon}
                alt="NGN logo"
                className="w-full h-full"
                width={480}
                height={480}
                priority
                quality={100}
              />
            </span>

            {/* kenya */}
            <div className="absolute lg:-bottom-[40px] md:-bottom-[30px] -bottom-[20px] lg:right-[60px] md:right-[60px] right-[30px]">
              <div className="relative">
                <span className="flex lg:size-[80px] md:size-[60px] size-[40px]">
                  <Image
                    src={KenyaIcon}
                    alt="Kenya logo"
                    className="w-full h-full"
                    width={240}
                    height={240}
                    priority
                    quality={100}
                  />
                </span>
                {/* badge */}
                <ComingSoonBadge
                  gradColor1="#FFCDE833"
                  gradColor2="#FFD3F32B"
                  BgColor="#FFB0B0"
                >
                  Coming Soon
                </ComingSoonBadge>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Background Pattern Overlay */}
      <div className="max-w-[73rem] mx-auto flex flex-col gap-4 items-center">
        <div className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-[1.25rem]">
          <h1 className="font-semibold text-3xl md:text-4xl lg:text-[64px] leading-[36px] md:leading-[48px] lg:leading-[66px] tracking-[-0.6px] md:tracking-[-1px] lg:tracking-[-1.24px] text-[#010721] text-center font-onest mb-1">
            Send Crypto.
            <br />
            Bank account gets paid. Instantly.
          </h1>
          <p className="font-400 text-sm md:text-base lg:text-xl tracking-[-0.22px] md:tracking-[-0.35px] lg:tracking-[-0.45px] text-[#6B7280] max-w-[550px] mx-auto text-center">
            <Highlighter action="underline" color="#EAFA35">
              Move crypto assets
            </Highlighter>{" "}
            directly into any{" "}
            <Highlighter action="highlight" color="#abedffff">
              bank account.
            </Highlighter>
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-center mb-6 md:mb-7 w-full md:w-auto">
          <Link
            href="/register"
            className="bg-[#EAFA35] text-[#0046A7] font-medium text-xs lg:text-sm h-10 md:h-14 rounded-lg py-2 md:py-[10px] px-4 md:px-6 lg:px-[24px] flex gap-2 md:gap-[12px] items-center align-middle tracking-[-0.15px] flex-shrink-0 transform transition-all duration-200 ease-in-out translate-y-0 hover:-translate-y-1 hover:shadow-[0_5px_0_0_#C2D120] active:translate-y-0 active:shadow-none"
          >
            Convert Crypto to Fiat Now
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
          <Link
            href="https://link.useresolva.io/download"
            target="_blank"
            className="bg-transparent border border-[#0046A7] text-[#0046A7] font-medium text-xs lg:text-sm h-10 md:h-14 rounded-lg py-2 md:py-[10px] px-4 md:px-6 lg:px-[24px] flex gap-2 md:gap-[12px] items-center align-middle tracking-[-0.15px] flex-shrink-0 active:shadow-none"
          >
            Download Mobile App
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
